import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface CompanyCaregiver {
  userId: string;
  fullName: string | null;
  avatarUrl: string | null;
}

export function usePatientCompany(patientId: string | undefined) {
  const { user } = useAuth();

  // Get company linked to this patient
  const { data: companyId, isLoading: companyLoading } = useQuery({
    queryKey: ['patient-company', patientId],
    queryFn: async () => {
      if (!patientId) return null;
      const { data, error } = await supabase
        .from('company_patient_assignments')
        .select('company_id')
        .eq('patient_id', patientId)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      if (error || !data) return null;
      return data.company_id;
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
  });

  // Get available employees (not yet linked to the patient)
  const { data: availableCaregivers = [], isLoading: caregiversLoading, refetch: refetchCaregivers } = useQuery({
    queryKey: ['available-company-caregivers', companyId, patientId],
    queryFn: async (): Promise<CompanyCaregiver[]> => {
      if (!companyId || !patientId) return [];

      // Get employees of the company
      const { data: employees, error: empErr } = await supabase
        .from('company_members')
        .select('user_id')
        .eq('company_id', companyId)
        .eq('role', 'employee')
        .eq('is_active', true);

      if (empErr || !employees?.length) return [];

      // Get user_ids already linked to this patient
      const { data: existingLinks } = await supabase
        .from('patient_user_links')
        .select('user_id')
        .eq('patient_id', patientId)
        .eq('active', true);

      const linkedUserIds = new Set((existingLinks || []).map(l => l.user_id));
      const availableUserIds = employees
        .map(e => e.user_id)
        .filter(uid => !linkedUserIds.has(uid));

      if (availableUserIds.length === 0) return [];

      // Fetch profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', availableUserIds);

      return (profiles || []).map(p => ({
        userId: p.user_id,
        fullName: p.full_name,
        avatarUrl: p.avatar_url,
      }));
    },
    enabled: !!companyId && !!patientId,
    staleTime: 30 * 1000,
  });

  return {
    companyId,
    isCompanyPatient: !!companyId,
    availableCaregivers,
    isLoading: companyLoading || caregiversLoading,
    refetchCaregivers,
  };
}
